import { Grid, Container, Typography, Button, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { DefaultPaper } from "@aclymatepackages/atoms";
import { formatDecimal } from "@aclymatepackages/formatters";
import {
  findConvertedProductWeights,
  calculateProductFootprintBreakdown
} from "@aclymatepackages/products";
import { ProductFootprintPieChart } from "@aclymatepackages/modules";

import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const ProductSummaryDisplayCard = ({
  footprintBreakdownKgs,
  totalFootprintKgs,
  label,
  footprintDifference,
  showReportButton
}) => {
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  return (
    <DefaultPaper>
      <Grid container direction="column" spacing={0}>
        <Grid item container justifyContent="center" spacing={1}>
          <Grid item>
            <Typography
              variant="h4"
              align="center"
              color={
                footprintDifference
                  ? footprintDifference > 0
                    ? "error"
                    : "secondary"
                  : "textPrimary"
              }
            >
              {label}:{" "}
              <span style={{ filter: isSubscribed ? "none" : "blur(4px)" }}>
                {isSubscribed ? formatDecimal(totalFootprintKgs) : "##"}
              </span>{" "}
              kgs
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip
              title={
                footprintDifference
                  ? `Your product's footprint will go ${
                      footprintDifference > 0 ? "up" : "down"
                    } by ${formatDecimal(Math.abs(footprintDifference))} kgs.`
                  : "This is your product's total carbon footprint."
              }
            >
              <HelpOutlineIcon />
            </Tooltip>
          </Grid>
        </Grid>
        <Grid item container justifyContent="center">
          <ProductFootprintPieChart
            footprintBreakdownKgs={footprintBreakdownKgs}
            totalFootprintKgs={totalFootprintKgs}
            blurValues={!isSubscribed}
          />
        </Grid>
        {showReportButton && (
          <Grid
            item
            container
            justifyContent="center"
            alignItems="flex-end"
            spacing={1}
          >
            <Grid item>{showReportButton}</Grid>
          </Grid>
        )}
      </Grid>
    </DefaultPaper>
  );
};

const ProductFootprintSummaryChart = ({
  originalFootprintBreakdownKgs,
  originalKgsCo2e,
  editedFootprintBreakdownKgs,
  editedKgsCo2e,
  onOpenReport
}) => {
  const withinEpsilon = (a, b) => Math.abs((a || 0) - (b || 0)) < 1e-9;

  const isEditedProductSame =
    withinEpsilon(editedKgsCo2e, originalKgsCo2e) &&
    Object.entries(editedFootprintBreakdownKgs || {}).every(([key, value]) =>
      withinEpsilon(value, (originalFootprintBreakdownKgs || {})[key])
    );

  return (
    <Container maxWidth="sm">
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <ProductSummaryDisplayCard
            footprintBreakdownKgs={originalFootprintBreakdownKgs}
            totalFootprintKgs={originalKgsCo2e}
            label={
              !isEditedProductSame
                ? "Original Product Footprint"
                : "Total Product Footprint"
            }
            showReportButton={
              isEditedProductSame && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onOpenReport}
                >
                  View Full Report
                </Button>
              )
            }
          />
        </Grid>
        {!isEditedProductSame && (
          <Grid item>
            <ProductSummaryDisplayCard
              footprintBreakdownKgs={editedFootprintBreakdownKgs}
              totalFootprintKgs={editedKgsCo2e}
              label="Edited product footprint"
              footprintDifference={editedKgsCo2e - originalKgsCo2e}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export const ProductFootprintLeftContent = ({
  basicInfo,
  components,
  packaging,
  electronics,
  route,
  onOpenReport,
  originalFootprintBreakdownKgs,
  originalKgsCo2e
}) => {
  const [{ emissionsFactors }] = useProductsEmissionsFactors();

  const editableProduct = {
    ...basicInfo,
    ...findConvertedProductWeights(basicInfo),
    components: components?.map((c) => ({
      ...c,
      productWeightUnit: c.productWeightUnit || basicInfo.productWeightUnit
    })),
    packaging,
    electronics,
    route
  };

  const {
    kgsCo2e: rawEditedKgsCo2e,
    footprintBreakdownKgs: editedFootprintBreakdownKgs
  } = calculateProductFootprintBreakdown(editableProduct, emissionsFactors);
  const editedKgsCo2e = Number.isNaN(rawEditedKgsCo2e) ? 0 : rawEditedKgsCo2e;

  return (
    <ProductFootprintSummaryChart
      originalFootprintBreakdownKgs={originalFootprintBreakdownKgs}
      editedFootprintBreakdownKgs={editedFootprintBreakdownKgs}
      originalKgsCo2e={originalKgsCo2e}
      editedKgsCo2e={editedKgsCo2e}
      onOpenReport={onOpenReport}
    />
  );
};
